/**
 * AssessorAnnotationsSection — assessor verdict on a register item.
 *
 * PR-Cluster-6-e (docs/ux-design-plan.md §10). Completes the trio
 * of assessor-collab surfaces on RegisterItemDetail (Q&A +
 * evidence-requests + annotations). Read-only for workspace members
 * — the annotation lives independently from the customer's own
 * status, so surfacing it here does NOT flip the item's
 * implemented/in_progress/… state.
 *
 * Annotations are written ONLY from assessor sessions (server-side
 * gate). One active annotation per (engagement, register_item);
 * re-annotating supersedes the prior (also enforced server-side).
 *
 * PR-Cluster-6-f: cross-engagement verdict-diff banner. When the
 * active verdicts across engagements don't agree (e.g. A said
 * "accepted" and B says "needs_remediation"), a Blocker-tone banner
 * sits at the top of the section. Pure client-side derivation.
 *
 * PR-Cluster-6-g: annotation history drawer per engagement. Each
 * card gets a "History for this engagement" affordance that lazy-
 * loads the full timeline of verdicts recorded by that engagement.
 *
 * PR-Cluster-6-h: owner dispute affordance. Workspace member
 * (owner / admin / editor) can file a counter-view on an active
 * verdict via the "Dispute verdict" affordance on each card. The
 * dispute is chained into the audit log and shows to the assessor
 * as a workpaper-visible dissent. Cards with an active dispute
 * render a `DISPUTED` badge next to the verdict pill, a full
 * <DisputePanel /> below the assessor's reason_text with the
 * dispute reason + filer + filed-at, and a "Withdraw dispute" CTA
 * for owner / admin / editor. One active dispute per annotation
 * (409 from server if you try to file a second).
 *
 * PR-Cluster-6-j: assessor-side "acknowledge" affordance. When the
 * session role is 'assessor', the <DisputePanel /> gains an
 * "Acknowledge dispute" CTA alongside (or in place of) the workspace
 * member's "Withdraw" CTA. Acknowledgment closes the dispute with
 * resolution='acknowledged' — the assessor has read the customer's
 * counter-view but chose not to re-annotate. Verdict is unchanged.
 */

import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Field, Spinner, Textarea } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../../lib/workspace';
import {
  acknowledgeAnnotationDispute,
  disputeAnnotation,
  getAnnotationHistoryForItem,
  listAnnotationsForItem,
  withdrawAnnotationDispute,
  type AssessorAnnotationDispute,
  type AssessorAnnotationRow,
  type AssessorAnnotationVerdict,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';

export function AssessorAnnotationsSection({ registerItemId }: { registerItemId: string }) {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  // Owner / admin / editor may file or withdraw a dispute per §7.9.
  // Viewer / advisor render the read-only card without the button.
  const canDispute = workspaceCtx
    ? workspaceCtx.role === 'owner' ||
      workspaceCtx.role === 'admin' ||
      workspaceCtx.role === 'editor'
    : false;
  // PR-Cluster-6-j: assessor sessions may acknowledge a dispute
  // (resolution = 'acknowledged') without re-annotating.
  const canAcknowledge = workspaceCtx?.role === 'assessor';

  const [rows, setRows] = useState<AssessorAnnotationRow[] | null>(null);
  const [disputes, setDisputes] = useState<Record<string, AssessorAnnotationDispute>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await listAnnotationsForItem(slug, registerItemId);
        if (!cancelled) {
          setRows(res.annotations);
          setDisputes(res.active_disputes ?? {});
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load annotations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, registerItemId, reload]);

  const refresh = useCallback(() => setReload((r) => r + 1), []);

  if (!slug) return null;

  // Suppress the whole section when there's nothing to say — the
  // register-item page already has enough vertical bulk with Q&A +
  // evidence-requests. When an annotation lands, the section
  // materializes in place.
  if (loading || (rows != null && rows.length === 0 && !error)) {
    if (loading) {
      return (
        <section
          aria-labelledby="assessor-annotations-heading"
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            padding: 'var(--space-4) var(--space-5)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
          }}
        >
          <h2 id="assessor-annotations-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            Assessor verdicts
          </h2>
          <Spinner label="Loading verdicts" />
        </section>
      );
    }
    // No verdicts + no error — render nothing so scan-mode readers
    // see three assessor-collab sections (Q&A / requests /
    // annotations) only when they carry real content.
    return null;
  }

  return (
    <section
      aria-labelledby="assessor-annotations-heading"
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 id="assessor-annotations-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            Assessor verdicts
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
            Each 3PAO's opinion on this control. Layered separately from your workspace's own
            status — the assessor's verdict does not change your item state.
          </p>
        </div>
        {rows && rows.length > 0 ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {rows.length} verdict{rows.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </header>

      {error ? (
        <Alert variant="error">{error}</Alert>
      ) : (
        <>
          {rows && rows.length > 1 ? <VerdictDiffBanner rows={rows} /> : null}
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {rows?.map((a) => (
              <AnnotationCard
                key={a.annotation_id}
                slug={slug}
                row={a}
                dispute={disputes[a.annotation_id] ?? null}
                canDispute={canDispute}
                canAcknowledge={canAcknowledge}
                onDisputeChanged={refresh}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ---- Cross-engagement verdict-diff banner ---------------------------

/**
 * When the active verdicts across engagements don't all agree, render
 * a Blocker-tone banner at the top of the section. Workspace members
 * scanning the page shouldn't have to compare each pill by eye — the
 * banner surfaces the disagreement + names the split.
 *
 * "Agree" means every active verdict is the same string. A single
 * disagreement (2 of 3 engagements agree, 1 dissents) still triggers
 * the banner — silence would let the dissenter get lost in the stack.
 *
 * Numbers below are counts of ACTIVE verdicts by kind (the parent
 * section already filters to non-superseded rows), so re-annotation
 * within one engagement is treated as "moved to the new verdict".
 */
function VerdictDiffBanner({ rows }: { rows: AssessorAnnotationRow[] }) {
  const counts = new Map<AssessorAnnotationVerdict, number>();
  for (const r of rows) {
    counts.set(r.verdict, (counts.get(r.verdict) ?? 0) + 1);
  }
  if (counts.size < 2) return null; // Every engagement agrees.
  const summary = Array.from(counts.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([verdict, n]) => `${n}× ${VERDICT_TONE[verdict].label.toLowerCase()}`)
    .join(' · ');
  return (
    <div
      role="alert"
      style={{
        border: '1px solid var(--band-red)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--danger-bg)',
        color: 'var(--danger-fg)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: '4px',
      }}
    >
      <strong
        style={{
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
        }}
      >
        Assessors disagree
      </strong>
      <span style={{ fontSize: 'var(--font-size-2xs)' }}>
        {rows.length} verdicts across engagements split{' '}
        <span style={{ fontFamily: 'var(--font-mono)' }}>{summary}</span>. Review each
        card below — the customer's own item status is unchanged, but the
        disagreement will surface in the audit workpaper.
      </span>
    </div>
  );
}

// ---- Annotation card -------------------------------------------------

function AnnotationCard({
  slug,
  row,
  dispute,
  canDispute,
  canAcknowledge,
  onDisputeChanged,
}: {
  slug: string;
  row: AssessorAnnotationRow;
  dispute: AssessorAnnotationDispute | null;
  canDispute: boolean;
  canAcknowledge: boolean;
  onDisputeChanged: () => void;
}) {
  const tone = VERDICT_TONE[row.verdict];
  const [showHistory, setShowHistory] = useState(false);
  const [showDisputeComposer, setShowDisputeComposer] = useState(false);
  return (
    <article
      style={{
        border: `1px solid ${tone.border}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: tone.bg,
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <VerdictPill verdict={row.verdict} />
        {dispute ? <DisputedBadge /> : null}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
          }}
          title={row.annotated_by}
        >
          {row.annotated_by}
        </span>
        <time dateTime={row.annotated_at} style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          {fmtDate(row.annotated_at)}
        </time>
      </header>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-2xs)',
          color: tone.body,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {row.reason_text}
      </p>
      {dispute ? (
        <DisputePanel
          slug={slug}
          dispute={dispute}
          canWithdraw={canDispute}
          canAcknowledge={canAcknowledge}
          onChanged={onDisputeChanged}
        />
      ) : null}
      <footer style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <span title={row.engagement_id} style={{ fontFamily: 'var(--font-mono)' }}>
          engagement {row.engagement_id.slice(0, 12)}…
        </span>
        <span aria-hidden>·</span>
        <span title={row.event_id} style={{ fontFamily: 'var(--font-mono)' }}>
          audit {row.event_id.slice(0, 12)}…
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {canDispute && !dispute ? (
            <button
              type="button"
              onClick={() => setShowDisputeComposer((s) => !s)}
              aria-expanded={showDisputeComposer}
              aria-controls={`dispute-composer-${row.annotation_id}`}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 0,
                padding: 0,
                color: 'var(--danger-fg)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-3xs)',
                fontWeight: 500,
              }}
            >
              {showDisputeComposer ? 'Cancel dispute' : 'Dispute verdict'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            aria-expanded={showHistory}
            aria-controls={`annotation-history-${row.annotation_id}`}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              padding: 0,
              color: 'var(--accent)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 500,
            }}
          >
            {showHistory ? 'Hide history' : 'History for this engagement'}
          </button>
        </div>
      </footer>
      {showDisputeComposer && !dispute ? (
        <DisputeComposer
          id={`dispute-composer-${row.annotation_id}`}
          slug={slug}
          annotationId={row.annotation_id}
          onCancel={() => setShowDisputeComposer(false)}
          onFiled={() => {
            setShowDisputeComposer(false);
            onDisputeChanged();
          }}
        />
      ) : null}
      {showHistory ? (
        <AnnotationHistoryDrawer
          id={`annotation-history-${row.annotation_id}`}
          slug={slug}
          registerItemId={row.register_item_id}
          engagementId={row.engagement_id}
          currentAnnotationId={row.annotation_id}
        />
      ) : null}
    </article>
  );
}

// ---- Disputed badge --------------------------------------------------

function DisputedBadge() {
  return (
    <span
      title="This verdict is under active dispute from this workspace."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
        border: '1px solid var(--band-amber)',
      }}
    >
      Disputed
    </span>
  );
}

// ---- Dispute panel (visible when a dispute is active) ---------------

function DisputePanel({
  slug,
  dispute,
  canWithdraw,
  canAcknowledge,
  onChanged,
}: {
  slug: string;
  dispute: AssessorAnnotationDispute;
  canWithdraw: boolean;
  canAcknowledge: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<'withdraw' | 'acknowledge' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const withdraw = async () => {
    if (!confirm('Withdraw this dispute? The disputed-badge will disappear and the assessor no longer sees the counter-view.')) {
      return;
    }
    setBusy('withdraw');
    setError(null);
    try {
      await withdrawAnnotationDispute(slug, dispute.dispute_id, {});
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Withdraw failed.');
      setBusy(null);
    }
  };
  const acknowledge = async () => {
    if (
      !confirm(
        'Acknowledge this dispute? The disputed-badge disappears and the workspace member sees you have read their counter-view. Your verdict is unchanged.',
      )
    ) {
      return;
    }
    setBusy('acknowledge');
    setError(null);
    try {
      await acknowledgeAnnotationDispute(slug, dispute.dispute_id, {});
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Acknowledge failed.');
      setBusy(null);
    }
  };
  return (
    <div
      role="region"
      aria-label="Active dispute on this verdict"
      style={{
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong
          style={{
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 700,
          }}
        >
          Workspace counter-view
        </strong>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
          }}
          title={dispute.disputed_by_user_id}
        >
          {dispute.disputed_by_user_id}
        </span>
        <time
          dateTime={dispute.created_at}
          style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}
        >
          Filed {fmtDate(dispute.created_at)}
        </time>
      </header>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--warning-fg)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {dispute.dispute_reason_text}
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {canAcknowledge || canWithdraw ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          {canAcknowledge ? (
            <Button
              variant="secondary"
              compact
              type="button"
              disabled={busy !== null}
              onClick={acknowledge}
            >
              {busy === 'acknowledge' ? 'Acknowledging…' : 'Acknowledge dispute'}
            </Button>
          ) : null}
          {canWithdraw ? (
            <Button
              variant="ghost"
              compact
              type="button"
              disabled={busy !== null}
              onClick={withdraw}
            >
              {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw dispute'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---- Dispute composer (visible when Dispute-verdict button opened) -

function DisputeComposer({
  id,
  slug,
  annotationId,
  onCancel,
  onFiled,
}: {
  id: string;
  slug: string;
  annotationId: string;
  onCancel: () => void;
  onFiled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (trimmed.length < 20) {
      setError('Reason must be at least 20 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await disputeAnnotation(slug, annotationId, { reason_text: trimmed });
      onFiled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Filing dispute failed.');
      setBusy(false);
    }
  };
  return (
    <form
      id={id}
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
      }}
    >
      <strong
        style={{
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 700,
        }}
      >
        File dispute
      </strong>
      <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--warning-fg)' }}>
        Explain why this verdict doesn't match your workspace's own position. The
        reason is chained into the audit log and visible to the assessor.
      </p>
      <Field id={`${id}-reason`} label="Reason (audit-logged)">
        <Textarea
          id={`${id}-reason`}
          required
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Attestation letter signed 2026-08-01 sits behind /evidence/EV-317 and the assessor may have missed it."
        />
      </Field>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || trimmed.length < 20}>
          {busy ? 'Filing…' : 'File dispute'}
        </Button>
      </div>
    </form>
  );
}

// ---- History drawer --------------------------------------------------

/**
 * Reveals the full annotation history for one (engagement,
 * register_item) pair — every prior verdict this engagement recorded
 * on this control, including superseded rows. Backend endpoint has
 * existed since PR-Cluster-6-e (GET /:register_item_id/history?
 * engagement_id=X); this drawer wires the SPA affordance called out
 * as deferred in that PR's close-out.
 *
 * Renders inline beneath the card that opened it so the reader
 * doesn't lose place. Loads on expand (not eagerly) — most cards
 * won't be expanded in a typical session. Table is scrollable when
 * the history is longer than the card viewport.
 */
function AnnotationHistoryDrawer({
  id,
  slug,
  registerItemId,
  engagementId,
  currentAnnotationId,
}: {
  id: string;
  slug: string;
  registerItemId: string;
  engagementId: string;
  currentAnnotationId: string;
}) {
  const [history, setHistory] = useState<AssessorAnnotationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAnnotationHistoryForItem(slug, registerItemId, engagementId)
      .then((res) => {
        if (!cancelled) setHistory(res.history);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, registerItemId, engagementId]);
  return (
    <div
      id={id}
      role="region"
      aria-label="Annotation history for this engagement"
      style={{
        marginTop: 'var(--space-2)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <strong
        style={{
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
          fontWeight: 700,
        }}
      >
        Verdict history — this engagement
      </strong>
      {loading ? (
        <Spinner label="Loading history" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : !history || history.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          No history rows returned.
        </p>
      ) : (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'grid',
            gap: 'var(--space-2)',
            maxHeight: '20rem',
            overflowY: 'auto',
          }}
        >
          {history.map((h) => (
            <HistoryRow key={h.annotation_id} row={h} isCurrent={h.annotation_id === currentAnnotationId} />
          ))}
        </ol>
      )}
    </div>
  );
}

function HistoryRow({ row, isCurrent }: { row: AssessorAnnotationRow; isCurrent: boolean }) {
  const tone = VERDICT_TONE[row.verdict];
  const superseded = row.superseded_at != null;
  return (
    <li
      style={{
        display: 'grid',
        gap: '4px',
        padding: '8px 12px',
        border: `1px solid ${isCurrent ? tone.border : 'var(--border)'}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-alt)',
        opacity: superseded && !isCurrent ? 0.75 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <VerdictPill verdict={row.verdict} />
        {isCurrent ? (
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
              color: 'var(--accent)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Current
          </span>
        ) : superseded ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            Superseded {fmtDate(row.superseded_at!)}
          </span>
        ) : null}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'right',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
            fontFamily: 'var(--font-mono)',
          }}
          title={row.annotated_by}
        >
          {row.annotated_by} · <time dateTime={row.annotated_at}>{fmtDate(row.annotated_at)}</time>
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {row.reason_text}
      </p>
    </li>
  );
}

const VERDICT_TONE: Record<
  AssessorAnnotationVerdict,
  { bg: string; border: string; body: string; label: string; pillBg: string; pillFg: string }
> = {
  accepted: {
    bg: 'var(--success-bg)',
    border: 'var(--band-green)',
    body: 'var(--success-fg)',
    label: 'Accepted',
    pillBg: 'var(--band-green)',
    pillFg: 'var(--text-inverse)',
  },
  needs_remediation: {
    bg: 'var(--danger-bg)',
    border: 'var(--band-red)',
    body: 'var(--danger-fg)',
    label: 'Needs remediation',
    pillBg: 'var(--band-red)',
    pillFg: 'var(--text-inverse)',
  },
  not_applicable: {
    bg: 'var(--surface-alt)',
    border: 'var(--border)',
    body: 'var(--text-muted)',
    label: 'Not applicable',
    pillBg: 'var(--surface-alt)',
    pillFg: 'var(--text-muted)',
  },
};

function VerdictPill({ verdict }: { verdict: AssessorAnnotationVerdict }) {
  const t = VERDICT_TONE[verdict];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.pillBg,
        color: t.pillFg,
        border: `1px solid ${t.border}`,
      }}
    >
      {t.label}
    </span>
  );
}
